<script setup lang="ts">
import type { StopItem } from '@/models/jobEdit';

import { computed, ref, watch } from 'vue';

import JobEditStopForm from '@/components/forms/JobEditStopForm.vue';
import { useJobEditStop } from '@/composables/jobEdit/useJobEditStop';
import { createEmptyStopItem } from '@/models/jobEdit';

export interface Props {
  initialData?: StopItem;
  stopNumber?: number;
  stopType?: string;
  totalStops?: number;
  editingOverride?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  initialData: createEmptyStopItem,
  stopNumber: 1,
  stopType: 'pickup',
  totalStops: 2,
  editingOverride: false,
});

const emit = defineEmits<{
  moveLeft: [event: Event];
  moveRight: [event: Event];
  delete: [event: Event];
}>();

const { initialData, setFormRef, refreshInitialData, validate, getFormData } =
  useJobEditStop(() => props.initialData);

const stopFormRef = ref<InstanceType<typeof JobEditStopForm> | null>(null);
watch(
  stopFormRef,
  (el) => {
    setFormRef(el);
  },
  { immediate: true },
);

const stopTypeNormalized = computed(() => props.stopType.toLowerCase());

const stopIcon = computed(() =>
  props.initialData.is_warehouse ? 'mdi-warehouse' : 'mdi-truck-outline',
);

const stopSubtitle = computed(() => {
  if (stopTypeNormalized.value === 'pickup') return 'Pickup from';
  if (stopTypeNormalized.value === 'delivery') return 'Deliver to';
  return `Stop ${props.stopNumber}`;
});

const canMoveLeft = computed(
  () => props.editingOverride && props.stopNumber > 1,
);
const canMoveRight = computed(
  () => props.editingOverride && props.stopNumber < props.totalStops,
);
const canDelete = computed(
  () =>
    props.editingOverride &&
    props.totalStops > 2 &&
    props.stopNumber === props.totalStops,
);

watch(
  () => props.initialData,
  () => {
    // Skip during editing — structural store mutations (swap, add stop) also
    // trigger this deep watch and would discard in-progress form changes.
    if (props.editingOverride) return;
    refreshInitialData();
  },
  { deep: true },
);

watch(
  () => props.editingOverride,
  (isEditing) => {
    // Refresh after editing ends — whether via Cancel or Save. By the time
    // this fires, restoreStops() or loadJob() has already updated the store,
    // so props.initialData contains the correct (restored/saved) data.
    if (!isEditing) refreshInitialData();
  },
);

defineExpose({ validate, getFormData });
</script>

<template>
  <div
    :class="[
      $style.jobEditStopSection,
      { [$style.editing]: props.editingOverride },
    ]"
    data-testid="jobEditStopSection"
  >
    <div :class="$style.sectionHeader">
      <div :class="$style.sectionTitleGroup">
        <v-icon :icon="stopIcon" size="20" :class="$style.stopIcon" />
        <div>
          <div :class="$style.sectionTitle">STOP {{ props.stopNumber }}</div>
          <div :class="$style.sectionSubtitle">{{ stopSubtitle }}</div>
        </div>
      </div>
      <div :class="$style.sectionActions">
        <v-btn
          v-if="canMoveLeft"
          icon
          variant="text"
          size="small"
          density="compact"
          @click.stop="emit('moveLeft', $event)"
        >
          <v-icon size="18">mdi-arrow-left</v-icon>
        </v-btn>
        <v-btn
          v-if="canMoveRight"
          icon
          variant="text"
          size="small"
          density="compact"
          @click.stop="emit('moveRight', $event)"
        >
          <v-icon size="18">mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          v-if="canDelete"
          icon
          variant="text"
          size="small"
          density="compact"
          color="error"
          @click.stop="emit('delete', $event)"
        >
          <v-icon size="18">mdi-delete-outline</v-icon>
        </v-btn>
      </div>
    </div>

    <JobEditStopForm
      ref="stopFormRef"
      :initial-data="initialData"
      :stop-number="props.stopNumber"
      :stop-type="props.stopType"
      :readonly="!props.editingOverride"
    />
  </div>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.jobEditStopSection {
  flex: 0 0 calc((100% - 24px) / 3);
  min-width: 0;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid colors.$color-gray-200;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.editing {
  border-color: colors.$color-brand-500;
}

.sectionHeader {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.sectionTitleGroup {
  display: flex;
  align-items: center;
  gap: 8px;
}

.stopIcon {
  color: colors.$color-text-secondary;
}

.sectionTitle {
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  color: colors.$color-text-primary;
  line-height: 1.2;
}

.sectionSubtitle {
  font-size: 12px;
  color: colors.$color-text-secondary;
  line-height: 1.2;
}

.sectionActions {
  display: flex;
  align-items: center;
  gap: 2px;
}
</style>
